"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Info as HelpCircle, ShieldCheck, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
const faqItems = [{
  id: "guaranteed-income",
  question: "How do fixed and fixed indexed annuities create guaranteed lifetime income?",
  answer: "Certain fixed and fixed indexed annuities can provide lifetime income through annuitization or through an available lifetime income rider.\n\nThe way income is calculated, when it can begin, whether a rider charge applies, and what happens to the remaining contract value depend on the specific annuity and contract provisions.\n\nGuarantees are subject to the terms of the annuity contract and the claims-paying ability of the issuing insurance company."
}, {
  id: "sequence-of-returns",
  question: "What is sequence of returns risk and how can you protect against it?",
  answer: "Sequence of returns risk is the risk that poor investment returns occur near the beginning of retirement while withdrawals are also being taken. Early losses combined with withdrawals can have a greater effect on how long invested assets last.\n\nThere are different ways to manage this risk. Depending on a person's circumstances, these may include maintaining liquid reserves, adjusting withdrawals, diversifying sources of retirement income, or using guaranteed-income products for a portion of retirement income.\n\nNo single approach is appropriate for everyone."
}, {
  id: "social-security-timing",
  question: "How should you coordinate Social Security timing with annuity payouts?",
  answer: "The age you begin Social Security retirement benefits affects the amount of your monthly benefit. Starting before full retirement age generally reduces the monthly benefit. Delaying benefits beyond full retirement age can increase the monthly benefit through delayed retirement credits.\n\nFor people born in 1943 or later, delayed retirement credits are 8% per year after full retirement age, calculated monthly, and stop accumulating at age 70.\n\nSome people may consider using other income or assets while delaying Social Security, but whether that approach makes sense depends on factors such as income needs, health and longevity considerations, other retirement resources, taxes, and personal circumstances.\n\nLearn more / Verify: Social Security Administration — Delayed Retirement Credits."
}, {
  id: "tax-considerations",
  question: "How are withdrawals and annuity distributions taxed during retirement?",
  answer: "The federal tax treatment of an annuity depends on factors including how the contract was funded, the type of account or contract, and how money is distributed.\n\nDistributions from qualified retirement arrangements funded with pre-tax money are generally taxable when distributed, although different rules can apply when after-tax contributions are present.\n\nFor a nonqualified annuity purchased with after-tax money, earnings generally grow tax-deferred. For many nonperiodic withdrawals taken before the annuity starting date, federal tax rules generally treat earnings as being withdrawn before the owner's investment in the contract. Different rules apply to annuity payments and certain other distributions.\n\nIndividual circumstances can affect tax treatment. DJTD Consulting does not provide tax advice.\n\nLearn more / Verify: IRS Publication 575 — Pension and Annuity Income."
}, {
  id: "inflation-protection",
  question: "How do fixed indexed annuities help protect purchasing power against inflation?",
  answer: "A fixed indexed annuity can earn interest based in part on the performance of a specified market index, subject to the contract's crediting method and provisions such as caps, participation rates, or spreads.\n\nIndex-linked interest may provide an opportunity for contract value to grow when positive interest is credited. However, credited interest is not guaranteed to keep pace with inflation, and a fixed indexed annuity should not be described as guaranteeing protection from inflation.\n\nFixed indexed annuities generally protect contract value from direct losses caused solely by declines in the referenced index, but withdrawals, surrender charges, optional rider charges, contract adjustments, and other provisions may affect the amount available.\n\nGuarantees are subject to the annuity contract and the claims-paying ability of the issuing insurance company."
}];
export default function RetirementIncome() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: prefersReducedMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="retirement-income" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: prefersReducedMotion ? 0.01 : 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-2xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="rounded-full border-border bg-card px-3.5 py-1 text-xs font-semibold tracking-wider text-foreground shadow-xs uppercase">
              <Sparkles className="size-3 text-chart-2" />
              Retirement income strategy
            </Badge>
          </div>

          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Retirement income questions and answers
          </h2>

          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Understand how guaranteed lifetime streams, withdrawal timing, and tax considerations work together to protect your post-career lifestyle.
          </p>

          <div className="mt-6 flex items-center justify-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <div className="h-px w-10 bg-border" />
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <div className="h-px w-10 bg-border" />
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <div className="h-px w-10 bg-border" />
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          </div>
        </motion.div>

        {/* Central FAQ Accordion Container */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="mx-auto max-w-[840px]">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqItems.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i}>
                <AccordionItem value={item.id} className="rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-chart-2/40 hover:shadow-md overflow-hidden">
                  <AccordionTrigger className="px-5 py-4 sm:px-6 sm:py-5 text-left font-default text-base sm:text-lg font-semibold text-card-foreground hover:no-underline hover:text-foreground">
                    <span className="flex items-center gap-3 pr-2">
                      <HelpCircle className="size-4.5 shrink-0 text-muted-foreground" />
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 sm:px-6 sm:pb-6 text-sm sm:text-base text-muted-foreground leading-relaxed border-t border-border/50 pt-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>

          {/* Supportive Clarity Callout Card */}
          <motion.div variants={itemVariants} className="mt-10 rounded-2xl border border-border bg-muted/40 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-card p-3 border border-border shrink-0 shadow-2xs">
                <ShieldCheck className="size-6 text-foreground" />
              </div>
              <div>
                <h3 className="font-default text-base sm:text-lg font-semibold text-foreground">
                  Need a personalized retirement income projection?
                </h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  We model custom carrier comparisons and guaranteed payout scenarios tailored to your timeline.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}